/* Built In Imports */
import { useContext, useEffect } from 'react';

/* External Imports */
import { Box, ChakraProvider } from '@chakra-ui/react';
import parser from 'html-react-parser';
import forEach from 'lodash/forEach';

/* Internal Imports */
/* Components */
import MidContainer from '@Layout/MidContainer';
import config from '@config';
import PageConfig from '@lb/Common/PageConfig';
import StructuredTextContent from '@lb/Common/StructuredTextContent';
import FaqExpandedSection from '@lb/components/Accordions/FaqExpandedSection';
import FaqSectionGroupV2 from '@lb/components/Accordions/FaqSectionGroupV2';
import TopBannerEventRitual from '@lb/components/Banners/TopBannerEventRitual';
import SimpleButton from '@lb/components/Buttons/SimpleButton';
import OfferingGroup from '@lb/components/Cards/OfferingGroup';
import RitualEmbed from '@lb/components/Embeds/RitualEmbed';
import RitualLocate from '@lb/components/Filter/RitualLocate';
import WrapLayout from '@lb/components/Layout/WrapLayout';
import LBContactUsCta from '@lb/components/Message/LBContactUsCta';
import Breadcrum from '@lb/components/Navigation/Breadcrum';
import RelatedLBSadhanaBringDeviHome from '@lb/components/Sliders/RelatedLBSadhanaBringDeviHome';
import HeadComponent from '@lb/components/Utility/HeadComponent';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@msr/components/Utility/Shared/SharedService';
import {
  fetchFooterDetailData,
  fetchHeaderMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/lbService';

/* Styles */
import RGallery from '@lb/components/Sliders/ritual/RGallery';
import classes from '@lb/components/Utility/STstyle.module.css';
import { lingaBhairaviTheme } from '@styles/theme';

/**
 * @param {object} pageData
 * @param lang
 * @param region
 * @returns
 */

const RitualDetail = ({ pageData, region, lang }) => {
  const authContext = useContext(AuthContext);

  let pageSummary = `<mark>${pageData?.summary}`?.split(' ');
  pageSummary[1] = `${pageSummary[1]}</mark>`;
  pageSummary = pageSummary.join(' ');

  // overrideRegistrationLogic -> true - dato will overwrite arpanam system data
  if (!pageData?.overrideRegistrationLogic) {
    if (pageData.arpanam?.ritual?.locations?.length) {
      pageData.globalRegLink = pageData.arpanam?.ritual?.regLink;
      pageData.locations = pageData.arpanam?.ritual?.locations?.filter(loc => {
        return loc.isAvailable === true;
      });
      pageData.isAvailableOnline = pageData.arpanam?.ritual?.isAvailableOnline;
      pageData.availableOnline =
        pageData.arpanam?.ritual?.isAvailable === true ||
        pageData.arpanam?.ritual?.isAvailableOnline === true;
    } else {
      pageData.locations = [];
      pageData.isAvailableOnline = false;
      pageData.availableOnline = false;
    }
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: pageData.title,
    description: pageData.summary,
    image: pageData.heroImage?.url,
    offers: {
      '@type': 'Offer',
      url: '',
    },
  };

  // adding arpanam data in related_ritual_content
  forEach(pageData?.related_ritual_content, card => {
    forEach(pageData?.arpanam?.rituals, ritual => {
      if (ritual?.arpanamId === card?.arpanamId) {
        card.visible = true;
        if (!pageData?.overrideRegistrationLogic) {
          if (!ritual?.locations.length) {
            card.visible = false;
          } else {
            let locations = [];
            forEach(ritual?.locations, loc => {
              locations.push({ title: loc });
            });
            card.locations = locations;
            card.ritual = ritual;
            card.isAvailableOnline = ritual?.isAvailableOnline;
          }
        }
      }
    });
  });

  useEffect(() => {
    authContext.RegionAndLang(region, lang);
  }, [authContext, region, lang]);

  return (
    <WrapLayout pageData={pageData}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData.availableLocales}
      >
        {' '}
      </HeadComponent>
      {pageData?.breadcrumbs?.length > 0 && (
        <Breadcrum
          page={pageData.breadcrumbs}
          alsoin={pageData.alsoin}
          url=""
          region={region}
          lang={lang}
        />
      )}

      <ChakraProvider resetCSS theme={lingaBhairaviTheme}>
        <Box
          as="section"
          background={`url(${config.imgPath}/d/46272/1682095879-white-texture-final.png)`}
          className="container-article"
          p="0"
        >
          <TopBannerEventRitual
            pageData={pageData}
            region={region}
            language={lang}
          />
          <MidContainer w="696px" px={{ base: '16px', md: '0' }}>
            <Box
              sx={{
                h2: {
                  fontSize: '26px',
                },
                h3: {
                  fontSize: '18px',
                },
              }}
              className={classes.LBStructured}
            >
              {pageSummary && parser(pageSummary)}
            </Box>
            {!pageData?.overrideRegistrationLogic && pageData.arpanam && (
              <RitualLocate pageData={pageData} region={region} lang={lang} />
            )}

            {pageData?.overrideRegistrationLogic &&
              !pageData.arpanam?.ritual?.arpanamId &&
              pageData.registerCallToAction?.length > 0 &&
              pageData?.registerCallToAction[0]._modelApiKey ===
                'linga_bhairavi_contact_us_cta' && (
                <LBContactUsCta
                  sectionContent={pageData?.registerCallToAction[0]}
                />
              )}

            {pageData?.overrideRegistrationLogic &&
              !pageData.arpanam?.ritual?.arpanamId &&
              pageData.registerCallToAction?.length > 0 &&
              pageData?.registerCallToAction[0]._modelApiKey ===
                'simple_button' && (
                <Box display="grid">
                  <SimpleButton
                    style={pageData?.registerCallToAction[0]}
                    region={region}
                    lang={lang}
                  />
                </Box>
              )}

            <Box
              fontFamily={'FedraSerifAStdBook'}
              className={classes.LBStructured}
              mt="30px"
            >
              {StructuredTextContent(pageData?.body, region, lang, 'ritual')}
            </Box>
          </MidContainer>

          <Box>
            <RGallery sectionContent={pageData?.imageGallery} />
          </Box>
          {pageData?.video?.url && (
            <RitualEmbed sectionContent={pageData?.video} pageData={pageData} />
          )}
          {pageData?.faqs[0]?._modelApiKey === 'faq_expanded_section' && (
            <FaqExpandedSection
              sectionContent={pageData?.faqs[0]}
              region={region}
              lang={lang}
            />
          )}

          {pageData?.faqs[0]?._modelApiKey === 'faq_section_group_v2' && (
            <MidContainer w="1330px">
              <FaqSectionGroupV2
                faqSection={pageData?.faqs[0]}
                region={region}
                lang={lang}
              />
            </MidContainer>
          )}

          {pageData?.offeringGroup?.length > 0 && (
            <OfferingGroup
              sectionContent={pageData?.offeringGroup[0]}
              region={region}
              lang={lang}
            />
          )}
          {pageData?.related_ritual_content?.length > 0 && (
            <RelatedLBSadhanaBringDeviHome
              sectionContent={{
                style: 'rituals_bring_devi_home_red',
                title: pageData?.ritualStaticContent?.json?.relatedTitle,
                description: {
                  value: {
                    schema: 'dast',
                    document: {
                      type: 'root',
                      children: [
                        {
                          type: 'paragraph',
                          children: [
                            {
                              type: 'span',
                              value:
                                pageData?.ritualStaticContent?.json
                                  ?.relatedDescription,
                            },
                          ],
                        },
                      ],
                    },
                  },
                },
                cta: [
                  {
                    linkUrl:
                      pageData?.ritualStaticContent?.json?.relatedURLAlias,
                    buttonText: pageData?.ritualStaticContent?.json?.relatedCTA,
                    buttonStyle: 'LB-Default',
                    _modelApiKey: 'simple_button',
                  },
                ],
                cards: pageData?.related_ritual_content,
              }}
              region={region}
              lang={lang}
            />
          )}
        </Box>
      </ChakraProvider>
      <PageConfig isLikeVisible={false} pageData={pageData} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const lbPaths = config.USE_LOCAL_URL
    ? [
        `/linga-bhairavi/in/en/rituals/devi-abhishekam`,
        `/linga-bhairavi/in/en/rituals/netram-arpanam`,
        `/linga-bhairavi/in/en/rituals/bhairavi-vilakku-seva`,
        `/linga-bhairavi/in/en/rituals/test`,
        `/linga-bhairavi/in/en/rituals/mala-arpanam`,
        `/linga-bhairavi/in/en/rituals/vastram-arpanam`,
        `/linga-bhairavi/in/en/rituals/mani-seva`,
        `/linga-bhairavi/np/en/rituals/mani-seva`,
        `/linga-bhairavi/in/en/rituals/mangalya-arpanam`,
        `/linga-bhairavi/in/en/rituals/raksha-kriya`,
        `/linga-bhairavi/in/en/rituals/mangalya-arpanam`,
        `/linga-bhairavi/in/en/rituals/purnima-abhishekam`,
        `/linga-bhairavi/in/en/rituals/thaipusam-offerings`,
        `/linga-bhairavi/in/en/rituals/navratri-offerings`,
        `/linga-bhairavi/in/en/rituals/bhairavi-punya-pooja`,
        `/linga-bhairavi/in/en/rituals/kalabhairava-shanti`,
        `/linga-bhairavi/in/en/rituals/kalabhairava-karma`,
        `/linga-bhairavi/np/en/rituals/devi-abhishekam`,
        `/linga-bhairavi/in/en/rituals/vivaha`,
        `/linga-bhairavi/in/en/rituals/mahalaya-amavasya-offerings`,
        `/linga-bhairavi/in/en/rituals/prahara-vilakku-seva`,
        `/linga-bhairavi/in/en/rituals/thaipusam-offerings`,
        `/linga-bhairavi/in/en/rituals/karna-veda-adult`,
        `/linga-bhairavi/in/en/rituals/klesha-nashana-kriya`,
        `/linga-bhairavi/in/en/rituals/valmeekam-sarpa-seva`,
        `/linga-bhairavi/np/en/rituals/karna-veda-child`,
        `/linga-bhairavi/np/en/rituals/raksha-kriya`,
        `/linga-bhairavi/np/en/rituals/raksha-kriya`,
        `/linga-bhairavi/in/en/rituals/chuda-karma`,
        `/linga-bhairavi/np/en/rituals/vastram-arpanam`,
        `/linga-bhairavi/in/en/rituals/guru-purnima-offerings`,
      ]
    : await getLandingPaths('L2', '/rituals/', 'linga_bhairavi_ritual');

  return {
    paths: lbPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, lang, ritual },
}) => {
  const topMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbtopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbmainmenu'
  );
  const footerData = await fetchFooterDetailData(
    getLocale(region, lang, 'lb'),
    'lbfooter'
  );
  const pageData = await fetchDetails(region, lang, `rituals/${ritual}`, {
    preview,
    data: previewData,
  });

  return {
    props: {
      headerMenuData: headerMenuData?.data || null,
      footerData: footerData?.data || null,
      topMenuData: topMenuData,
      pageData: pageData || null,
      isPreview: preview ? true : false,
      region: region,
      lang: lang,
      menuType: 'lb',
    },
  };
};

export default RitualDetail;
